import { useEffect, useState } from "react";
import { log } from "../utils/logger";
import { X, Download, Share2, Plus } from "lucide-react";

const DISMISS_KEY = "pwaInstallDismissedAt";
const DISMISS_DAYS = 7;

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

function isIos() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  const iOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  const isIpadOS = navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
  return iOS || isIpadOS;
}

function recentlyDismissed() {
  try {
    const ts = Number(localStorage.getItem(DISMISS_KEY) || 0);
    if (!ts) return false;
    return Date.now() - ts < DISMISS_DAYS * 24 * 60 * 60 * 1000;
  } catch {
    return false;
  }
}

export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [showIosSheet, setShowIosSheet] = useState(false);

  useEffect(() => {
    if (isStandalone() || recentlyDismissed()) return;

    // Chrome / Android / Edge: capture the beforeinstallprompt event
    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);

    // iOS Safari: no event fires — show a manual banner after a short delay
    let iosTimer;
    if (isIos()) {
      iosTimer = setTimeout(() => setVisible(true), 4000);
    }

    const onInstalled = () => {
      setVisible(false);
      setDeferred(null);
    };
    window.addEventListener("appinstalled", onInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
      if (iosTimer) clearTimeout(iosTimer);
    };
  }, []);

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch (err) {
      log.warn("Install prompt: storage unavailable", err);
    }
    setVisible(false);
    setShowIosSheet(false);
  };

  const handleInstall = async () => {
    if (deferred) {
      deferred.prompt();
      const choice = await deferred.userChoice.catch(() => null);
      setDeferred(null);
      setVisible(false);
      if (choice?.outcome !== "accepted") dismiss();
      return;
    }
    // iOS path — show the "Add to Home Screen" instructions
    if (isIos()) setShowIosSheet(true);
  };

  if (!visible) return null;

  return (
    <>
      <div
        data-testid="pwa-install-banner"
        className="fixed inset-x-0 bottom-0 z-[60] px-3 pb-3 sm:pb-4"
        style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 12px)" }}
      >
        <div className="mx-auto flex max-w-md items-center gap-3 rounded-2xl border-2 border-[var(--c-border)] bg-[var(--c-surface)] p-3 shadow-[4px_4px_0px_var(--c-shadow)]">
          <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl border-2 border-[var(--c-border)] bg-[#1E3A8A]">
            <Download className="h-5 w-5 text-white" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="font-heading text-sm font-bold text-[var(--c-text)]">Install the app</p>
            <p className="text-xs text-[var(--c-muted)]">Add Texas LLC Guide to your home screen for a full-screen, offline-ready experience.</p>
          </div>
          <button
            data-testid="pwa-install-button"
            onClick={handleInstall}
            className="flex-shrink-0 rounded-full border-2 border-[var(--c-border)] bg-[#1E3A8A] px-3 py-1.5 text-xs font-bold text-white shadow-[2px_2px_0px_var(--c-shadow)] transition-all hover:-translate-y-0.5"
          >
            Install
          </button>
          <button
            data-testid="pwa-install-dismiss"
            onClick={dismiss}
            aria-label="Dismiss"
            className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-[var(--c-muted)] hover:bg-[var(--c-bg)]"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {showIosSheet && (
        <div
          data-testid="pwa-ios-instructions"
          className="fixed inset-0 z-[70] flex items-end justify-center bg-black/50 sm:items-center"
          onClick={dismiss}
        >
          <div
            className="w-full max-w-md rounded-t-2xl border-2 border-[var(--c-border)] bg-[var(--c-surface)] p-5 shadow-[4px_4px_0px_var(--c-shadow)] sm:rounded-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-3 flex items-center justify-between">
              <h3 className="font-heading text-lg font-black text-[var(--c-text)]">Add to Home Screen</h3>
              <button onClick={dismiss} className="flex h-8 w-8 items-center justify-center rounded-full text-[var(--c-muted)]" aria-label="Close">
                <X className="h-4 w-4" />
              </button>
            </div>
            <ol className="space-y-3 text-sm text-[var(--c-text)]">
              <li className="flex items-start gap-3">
                <span className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full border-2 border-[var(--c-border)] bg-[#1E3A8A] text-xs font-bold text-white">1</span>
                <span className="flex items-center gap-1.5">Tap the <Share2 className="inline h-4 w-4 text-[#1E3A8A]" /> <strong>Share</strong> button at the bottom of Safari.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full border-2 border-[var(--c-border)] bg-[#1E3A8A] text-xs font-bold text-white">2</span>
                <span className="flex items-center gap-1.5">Scroll down and tap <Plus className="inline h-4 w-4 text-[#1E3A8A]" /> <strong>Add to Home Screen</strong>.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full border-2 border-[var(--c-border)] bg-[#1E3A8A] text-xs font-bold text-white">3</span>
                <span>Tap <strong>Add</strong> — the Texas LLC Guide icon will appear on your home screen.</span>
              </li>
            </ol>
          </div>
        </div>
      )}
    </>
  );
}
